import React from "react";
import axios from "axios";
import moment from "moment";
import { Card } from "ktm-ui";
import { Table } from "react-bootstrap";
import CustomPagination from "components/CustomPagination";

interface PlanAdminLog {
  id: string;
  planAdminUserId: string;
  planAdminName: string;
  planAdminEmail: string;
  action: string;
  field: string;
  oldValue: string;
  newValue: string;
  note: string;
  createdAt: string;
  changedBy: string;
}

interface Props {
  policy_id: string;
  refreshKey?: number;
}

interface State {
  logs: PlanAdminLog[];
  isHistoryVisible: boolean;
  currentPage: number;
}

const ITEMS_PER_PAGE = 8;

export default class PlanAdminLogs extends React.Component<Props, State> {
  state: State = {
    logs: [],
    isHistoryVisible: true,
    currentPage: 1,
  };

  componentDidMount() {
    this.getLogs();
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.refreshKey !== this.props.refreshKey) {
      this.getLogs();
    }
  }

  getLogs = () => {
    axios
      .get("/api/assignplanadmin/getplanadminlogs?policyId=" + this.props.policy_id)
      .then((res) => {
        this.setState({ logs: res.data, currentPage: 1 });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  toggleHistory = () => {
    this.setState({ isHistoryVisible: !this.state.isHistoryVisible });
  };

  setCurrentPage = (page: number) => {
    this.setState({ currentPage: page });
  };

  renderChange(log: PlanAdminLog) {
    if (!log.field) {
      return log.note || "";
    }

    const from = log.oldValue ? log.oldValue : "—";
    const to = log.newValue ? log.newValue : "—";

    return `${log.field}: ${from} → ${to}`;
  }

  render() {
    const { logs, currentPage } = this.state;

    const indexOfLastRecord = currentPage * ITEMS_PER_PAGE;
    const indexOfFirstRecord = indexOfLastRecord - ITEMS_PER_PAGE;
    const currentRecords = logs.slice(indexOfFirstRecord, indexOfLastRecord);
    const nPages = Math.ceil(logs.length / ITEMS_PER_PAGE);

    const historyTable = (
      <div className="history-content">
        <Table hover className="pa-table" style={{ backgroundColor: "white", width: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: "170px" }}>Date</th>
              <th style={{ width: "250px" }}>Plan Admin</th>
              <th style={{ width: "170px" }}>Action</th>
              <th>Change</th>
              <th style={{ width: "170px" }}>Changed By</th>
            </tr>
          </thead>
          <tbody>
            {currentRecords.map((log) => (
              <tr key={log.id}>
                <td>{moment(log.createdAt).format("YYYY-MM-DD HH:mm:ss")}</td>
                <td>{log.planAdminEmail || log.planAdminName}</td>
                <td>{log.action}</td>
                <td>{this.renderChange(log)}</td>
                <td>{log.changedBy}</td>
              </tr>
            ))}
          </tbody>
        </Table>
        {nPages > 1 && (
          <div
            className="pagination-container"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "10px",
              marginBottom: "10px",
            }}
          >
            <div className="pagination-info">
              <span>
                Showing {indexOfFirstRecord + 1} -{" "}
                {Math.min(indexOfLastRecord, logs.length)} of {logs.length} entries
              </span>
            </div>
            <CustomPagination
              nPages={nPages}
              currentPage={currentPage}
              setCurrentPage={this.setCurrentPage}
            />
          </div>
        )}
      </div>
    );

    const emptyState = (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80px",
        }}
      >
        <span style={{ fontWeight: "bold" }}>No plan admin changes recorded yet.</span>
      </div>
    );

    return (
      <div style={{ marginTop: 8 }}>
        <Card
          label="Plan Admin History"
          actions={
            <button
              onClick={this.toggleHistory}
              className={`toggle-button ${this.state.isHistoryVisible ? "open" : ""}`}
            >
              <span className="arrow"></span>
            </button>
          }
        >
          {this.state.isHistoryVisible && (logs.length === 0 ? emptyState : historyTable)}
        </Card>
      </div>
    );
  }
}
